import { db } from '../firebaseConfig';
import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, writeBatch } from 'firebase/firestore';
import { Sancion, ResultadoCarrera } from '../models/types';
import { CarreraServiceFirestore } from './CarreraServiceFirestore';

export class SancionServiceFirestore {
  private collectionName = 'sanciones';
  
  // Obtener todas las sanciones
  async getAll(): Promise<Sancion[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Sancion));
    } catch (error) {
      console.error('Error al obtener sanciones:', error);
      return [];
    }
  }

  // Obtener una sanción por su ID
  async getById(id: string): Promise<Sancion | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Sancion;
      }
      
      return null;
    } catch (error) {
      console.error(`Error al obtener sanción con ID ${id}:`, error);
      return null;
    }
  }

  // Obtener sanciones por socio
  async getBySocio(socioId: string): Promise<Sancion[]> {
    try {
      const q = query(collection(db, this.collectionName), where('socioId', '==', socioId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Sancion));
    } catch (error) {
      console.error(`Error al obtener sanciones del socio ${socioId}:`, error);
      return [];
    }
  }

  // Obtener sanciones por carrera
  async getByCarrera(carreraId: string): Promise<Sancion[]> {
    try {
      const q = query(collection(db, this.collectionName), where('carreraId', '==', carreraId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Sancion));
    } catch (error) {
      console.error(`Error al obtener sanciones de la carrera ${carreraId}:`, error);
      return [];
    }
  }

  // Crear una nueva sanción
  async create(sancion: Omit<Sancion, 'id'>): Promise<Sancion | null> {
    try {
      // Crear la sanción en Firestore
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...sancion,
        createdAt: new Date().toISOString()
      });
      
      const newSancion = {
        id: docRef.id,
        ...sancion
      } as Sancion;
      
      // Si la sanción está asociada a una carrera, actualizar el resultado
      if (sancion.carreraId) {
        await this.aplicarSancionACarrera(newSancion);
      }
      
      return newSancion;
    } catch (error) {
      console.error('Error al crear sanción:', error);
      return null;
    }
  }

  // Actualizar una sanción existente
  async update(id: string, sancionData: Partial<Sancion>): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.error(`La sanción con ID ${id} no existe.`);
        return false;
      }
      
      const oldSancion = {
        id: docSnap.id,
        ...docSnap.data()
      } as Sancion;
      
      // Actualizar la sanción
      await updateDoc(docRef, {
        ...sancionData,
        updatedAt: new Date().toISOString()
      });
      
      // Si cambió la carrera o los puntos de penalización, actualizar los resultados
      if (
        (sancionData.carreraId && sancionData.carreraId !== oldSancion.carreraId) ||
        (sancionData.puntosPenalizacion !== undefined && sancionData.puntosPenalizacion !== oldSancion.puntosPenalizacion)
      ) {
        // Si tenía una carrera anterior, quitar la sanción
        if (oldSancion.carreraId) {
          await this.quitarSancionDeCarrera(oldSancion);
        }
        
        // Si tiene una nueva carrera, aplicar la sanción
        if (sancionData.carreraId) {
          const updatedSancion = {
            ...oldSancion,
            ...sancionData
          } as Sancion;
          
          await this.aplicarSancionACarrera(updatedSancion);
        }
      }
      
      return true;
    } catch (error) {
      console.error(`Error al actualizar sanción con ID ${id}:`, error);
      return false;
    }
  }

  // Eliminar una sanción
  async delete(id: string): Promise<boolean> {
    try {
      // Obtener la sanción antes de eliminarla
      const sancion = await this.getById(id);
      if (!sancion) {
        console.error(`La sanción con ID ${id} no existe.`);
        return false;
      }
      
      // Si la sanción está asociada a una carrera, quitar la sanción
      if (sancion.carreraId) {
        await this.quitarSancionDeCarrera(sancion);
      }
      
      // Eliminar la sanción
      await deleteDoc(doc(db, this.collectionName, id));
      
      return true;
    } catch (error) {
      console.error(`Error al eliminar sanción con ID ${id}:`, error);
      return false;
    }
  }

  // Aplicar una sanción a una carrera
  private async aplicarSancionACarrera(sancion: Sancion): Promise<boolean> {
    try {
      const carreraService = CarreraServiceFirestore;
      const carrera = await carreraService.getById(sancion.carreraId);
      
      if (!carrera) {
        console.error(`La carrera con ID ${sancion.carreraId} no existe.`);
        return false;
      }
      
      // Buscar el resultado del piloto
      const resultadoIndex = carrera.resultados.findIndex((r: ResultadoCarrera) => r.socioId === sancion.socioId);
      if (resultadoIndex === -1) {
        console.error(`El piloto con ID ${sancion.socioId} no tiene resultados en la carrera ${sancion.carreraId}.`);
        return false;
      }
      
      // Actualizar el resultado
      const resultado = carrera.resultados[resultadoIndex];
      
      // Verificar si la sanción ya está aplicada
      if (!resultado.sancionesAplicadas) {
        resultado.sancionesAplicadas = [];
      }
      
      if (!resultado.sancionesAplicadas.includes(sancion.id)) {
        resultado.sancionesAplicadas.push(sancion.id);
      }
      
      // Actualizar puntos totales
      if (resultado.puntosTotales === undefined) {
        resultado.puntosTotales = 0;
      }
      resultado.puntosTotales += sancion.puntosPenalizacion;
      
      // Guardar los cambios
      carrera.resultados[resultadoIndex] = resultado;
      await carreraService.update(carrera.id, { resultados: carrera.resultados });
      
      return true;
    } catch (error) {
      console.error(`Error al aplicar sanción a carrera:`, error);
      return false;
    }
  }

  // Quitar una sanción de una carrera
  private async quitarSancionDeCarrera(sancion: Sancion): Promise<boolean> {
    try {
      const carreraService = CarreraServiceFirestore;
      const carrera = await carreraService.getById(sancion.carreraId);
      
      if (!carrera) {
        console.error(`La carrera con ID ${sancion.carreraId} no existe.`);
        return false;
      }
      
      // Buscar el resultado del piloto
      const resultadoIndex = carrera.resultados.findIndex((r: ResultadoCarrera) => r.socioId === sancion.socioId);
      if (resultadoIndex === -1) {
        console.error(`El piloto con ID ${sancion.socioId} no tiene resultados en la carrera ${sancion.carreraId}.`);
        return false;
      }
      
      // Actualizar el resultado
      const resultado = carrera.resultados[resultadoIndex];
      
      // Quitar la sanción
      if (resultado.sancionesAplicadas) {
        resultado.sancionesAplicadas = resultado.sancionesAplicadas.filter((id: string) => id !== sancion.id);
      }
      
      // Restar los puntos de penalización (sumar si eran negativos)
      if (resultado.puntosTotales !== undefined) {
        resultado.puntosTotales -= sancion.puntosPenalizacion;
      }
      
      // Guardar los cambios
      carrera.resultados[resultadoIndex] = resultado;
      await carreraService.update(carrera.id, { resultados: carrera.resultados });
      
      return true;
    } catch (error) {
      console.error(`Error al quitar sanción de carrera:`, error);
      return false;
    }
  }

  // Aplicar sanciones automáticas basadas en reglas configurables
  // Verificar si ya existe una sanción automática para un piloto en una carrera específica
  async existeSancionAutomatica(socioId: string, carreraId: string, codigoSancion: string): Promise<boolean> {
    try {
      const sancionesCollection = collection(db, 'sanciones');
      const q = query(
        sancionesCollection,
        where('socioId', '==', socioId),
        where('carreraId', '==', carreraId),
        where('codigoSancion', '==', codigoSancion),
        where('automatica', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error al verificar si existe sanción automática:', error);
      return false;
    }
  }
  
  async aplicarSancionesAutomaticas(carreraId: string): Promise<number> {
    try {
      const carreraService = CarreraServiceFirestore;
      const carrera = await carreraService.getById(carreraId);
      
      if (!carrera) {
        console.error(`La carrera con ID ${carreraId} no existe.`);
        return 0;
      }
      
      // Obtener las reglas activas de sanciones automáticas
      const { reglaSancionAutomaticaServiceFirestore } = await import('./ReglaSancionAutomaticaServiceFirestore');
      const reglasActivas = await reglaSancionAutomaticaServiceFirestore.getActivas();
      
      if (reglasActivas.length === 0) {
        console.log('No hay reglas de sanciones automáticas activas.');
        return 0;
      }
      
      let sancionesAplicadas = 0;
      
      // Aplicar cada regla activa a los resultados de la carrera
      for (const resultado of carrera.resultados) {
        for (const regla of reglasActivas) {
          let aplicarSancion = false;
          
          // Evaluar la condición de la regla
          switch (regla.condicion) {
            case 'dnf':
              aplicarSancion = resultado.dnf;
              console.log(`DEBUG - Evaluando regla DNF para piloto ${resultado.socioId}: dnf=${resultado.dnf}, aplicarSancion=${aplicarSancion}`);
              break;
              
            case 'no_participacion':
              // Usar la propiedad noParticipacion para determinar si el piloto no se presentó
              console.log(`DEBUG - Evaluando regla NO_PARTICIPACION para piloto ${resultado.socioId}: noParticipacion=${resultado.noParticipacion}`);
              aplicarSancion = resultado.noParticipacion === true;
              console.log(`DEBUG - Resultado evaluación NO_PARTICIPACION: aplicarSancion=${aplicarSancion}`);
              break;
              
            case 'incidentes':
              // Si el piloto supera el umbral de incidentes configurado
              if (regla.parametros?.umbralIncidentes && resultado.incidentes !== undefined) {
                aplicarSancion = resultado.incidentes > regla.parametros.umbralIncidentes;
              }
              break;
              
            case 'salida_pista':
              // Si el piloto supera el umbral de salidas de pista configurado
              if (regla.parametros?.umbralSalidasPista && resultado.salidasPista !== undefined) {
                aplicarSancion = resultado.salidasPista > regla.parametros.umbralSalidasPista;
              }
              break;
              
            // Otras condiciones pueden ser implementadas según sea necesario
            default:
              aplicarSancion = false;
          }
          
          // Si se cumple la condición, verificar si ya existe una sanción automática para este piloto y condición
          if (aplicarSancion) {
            const codigoSancion = `AUTO-${regla.condicion.toUpperCase()}`;
            
            // Verificar si ya existe una sanción automática para este piloto y condición
            const existeSancion = await this.existeSancionAutomatica(resultado.socioId, carrera.id, codigoSancion);
            
            if (!existeSancion) {
              // Crear una sanción automática según la regla solo si no existe ya
              const sancion: Omit<Sancion, 'id'> = {
                nombre: regla.nombre,
                descripcion: `${regla.descripcion} (Aplicada automáticamente)`,
                puntosPenalizacion: regla.puntosPenalizacion,
                socioId: resultado.socioId,
                carreraId: carrera.id,
                fechaAplicacion: new Date().toISOString().split('T')[0],
                automatica: true,
                codigoSancion: codigoSancion
              };
              
              const createdSancion = await this.create(sancion);
              if (createdSancion) {
                sancionesAplicadas++;
              }
            } else {
              console.log(`Ya existe una sanción automática para el piloto ${resultado.socioId} en la carrera ${carrera.id} con código ${codigoSancion}`);
            }
            
            // Romper el bucle de reglas para este piloto si ya se aplicó una sanción
            // (para evitar múltiples sanciones por el mismo motivo)
            break;
          }
        }
      }
      
      return sancionesAplicadas;
    } catch (error) {
      console.error(`Error al aplicar sanciones automáticas:`, error);
      return 0;
    }
  }
}

// Exportar una instancia del servicio
export const sancionServiceFirestore = new SancionServiceFirestore();
